import { ERROR_CODES, type ErrorCode } from "@salesboard/contracts";
import { ApiClientError } from "@/lib/api";
import type { MessageKey, Translator } from "./index";

/**
 * Codes the client has better words for than the server does.
 *
 * **Deliberately partial.** The error envelope carries a machine `code` *and* a human
 * `message`, and for some codes the server's message is the more useful of the two: a
 * `NOT_FOUND` says *which* thing does not exist, and a `VALIDATION_FAILED` names the field and
 * the rule. Replacing those with a generic Czech sentence would translate the words and lose
 * the information.
 *
 * So the map covers the codes whose server text is generic anyway — a session that expired, a
 * refusal, a rate limit, a dependency that is down — and everything else falls back to the
 * server's own English message. That is the honest arrangement: translating on the server
 * would mean threading the requester's locale through every error path in the API for the
 * benefit of two locales, and a client-side map that pretended to cover every code would
 * silently discard specifics.
 */
export const ERROR_MESSAGE_KEYS: Partial<Record<ErrorCode, MessageKey>> = {
  [ERROR_CODES.UNAUTHENTICATED]: "error.UNAUTHENTICATED",
  [ERROR_CODES.FORBIDDEN]: "error.FORBIDDEN",
  [ERROR_CODES.CONFLICT]: "error.CONFLICT",
  [ERROR_CODES.STALE_VERSION]: "toast.staleTitle",
  [ERROR_CODES.RATE_LIMITED]: "error.RATE_LIMITED",
  [ERROR_CODES.UNAVAILABLE]: "error.UNAVAILABLE",
  [ERROR_CODES.INTERNAL]: "error.INTERNAL",
};

/**
 * What to show a user for a failure.
 *
 * @param fallback What to say when the failure was not an API error at all — a thrown
 *   `TypeError`, an aborted fetch — where there is no server message to fall back to.
 */
export function apiErrorMessage(t: Translator, error: unknown, fallback: string): string {
  if (!(error instanceof ApiClientError)) return fallback;

  const key = ERROR_MESSAGE_KEYS[error.code];
  // `t` on a mapped key is always a plain string — every entry in the map points at one —
  // but the translator is generic, so the call is made through the narrow overload.
  if (key) return t(key as "error.INTERNAL");

  // Unmapped: the server's message carries the specifics, so it is the better answer even
  // though it is English.
  return error.message;
}
